// ABOUTME: Lists the user's MCP connections and exposes current administrator enablement controls.
// ABOUTME: Reloads the selected company after every mutation and never displays OAuth secrets.
import React from "react";
import {
    Box,
    Button,
    FormControlLabel,
    Paper,
    Switch,
    Typography
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useAuthState } from "_ReactContext/AuthContext";
import Page from "Pages/_Layouts/DefaultInternal";
import { mcpPanelCall } from "./api";
import McpConnectionSetup from "./McpConnectionSetup";
import { scopeLabels } from "./McpConsent";
interface State {
    companyId: string;
    companyName: string;
    mcpEnabled: boolean;
    canManage: boolean;
    globalEnabled: boolean;
    nextCursor?: string;
    connections: {
        connectionId: string;
        clientId: string;
        mine: boolean;
        active: boolean;
        scopes: string[];
        expiresAt: number;
    }[];
}
export default function McpConnections() {
    const [t] = useTranslation("MCP", { useSuspense: false });
    const { company, reloadCompanyAndPermissionData } = useAuthState();
    const companyId = String(company.companyId || "");
    const [state, setState] = React.useState<State | null>(null);
    const [revision, setRevision] = React.useState(0);
    const [page, setPage] = React.useState<{
        companyId: string;
        cursors: string[];
    }>({ companyId, cursors: [] });
    const cursors = page.companyId === companyId ? page.cursors : [];
    const cursor = cursors[cursors.length - 1];
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(false);
    React.useEffect(() => {
        setPage((current) =>
            current.companyId === companyId
                ? current
                : { companyId, cursors: [] }
        );
    }, [companyId]);
    React.useEffect(() => {
        const controller = new AbortController();
        setState(null);
        setError(false);
        if (companyId)
            void mcpPanelCall<State>(
                "/panel/mcp/state",
                { companyId, ...(cursor ? { cursor } : {}) },
                controller.signal
            )
                .then((value) => {
                    if (
                        !controller.signal.aborted &&
                        value.companyId === companyId
                    )
                        setState(value);
                })
                .catch(() => {
                    if (!controller.signal.aborted) setError(true);
                });
        return () => controller.abort();
    }, [companyId, cursor, revision]);
    async function mutate(path: string, body: Record<string, unknown>) {
        if (busy || !state || state.companyId !== companyId) return;
        setBusy(true);
        setError(false);
        try {
            await mcpPanelCall(path, { ...body, companyId });
            await reloadCompanyAndPermissionData();
            setPage({ companyId, cursors: [] });
            setRevision((current) => current + 1);
        } catch {
            setError(true);
        } finally {
            setBusy(false);
        }
    }
    return (
        <Page featureName={t("connectionsTitle")}>
            <Paper>
                <Box p={3}>
                    <Typography paragraph>
                        {t("connectionsDescription")}
                    </Typography>
                    {error && (
                        <Typography color="error" role="alert">
                            {t("unavailable")}
                        </Typography>
                    )}
                    {state && (
                        <>
                            <Typography variant="h6">
                                {state.companyName}
                            </Typography>
                            {!state.globalEnabled && (
                                <Typography paragraph>
                                    {t("globalDisabled")}
                                </Typography>
                            )}
                            {state.canManage && (
                                <FormControlLabel
                                    label={t("enableCompany")}
                                    control={
                                        <Switch
                                            checked={state.mcpEnabled}
                                            disabled={busy}
                                            onChange={(event) =>
                                                mutate("/panel/mcp/enable", {
                                                    enabled:
                                                        event.target.checked
                                                })
                                            }
                                        />
                                    }
                                />
                            )}
                            {!state.mcpEnabled && (
                                <Typography paragraph>
                                    {t("companyDisabled")}
                                </Typography>
                            )}
                            <McpConnectionSetup
                                apiOrigin={import.meta.env.VITE_MCP_API_ORIGIN}
                            />
                            {state.connections.length === 0 && (
                                <Typography>{t("noConnections")}</Typography>
                            )}
                            {state.connections.map((connection) => (
                                <Box
                                    key={connection.connectionId}
                                    mt={2}
                                    p={2}
                                    border={1}
                                    borderColor="grey.300"
                                >
                                    <Typography>
                                        {connection.clientId}
                                    </Typography>
                                    <Typography variant="body2">
                                        {t(
                                            connection.mine
                                                ? "mine"
                                                : "companyConnection"
                                        )}{" "}
                                        ·{" "}
                                        {t(
                                            connection.active
                                                ? "active"
                                                : "inactive"
                                        )}
                                    </Typography>
                                    <Typography variant="body2">
                                        {connection.scopes
                                            .map((scope) =>
                                                t(
                                                    scopeLabels[scope] ||
                                                        "scopeUnavailable"
                                                )
                                            )
                                            .join(" · ")}
                                    </Typography>
                                    <Button
                                        disabled={busy || !connection.active}
                                        onClick={() =>
                                            mutate("/panel/mcp/revoke", {
                                                connectionId:
                                                    connection.connectionId
                                            })
                                        }
                                    >
                                        {t("revoke")}
                                    </Button>
                                </Box>
                            ))}
                            <Box mt={2}>
                                <Button
                                    disabled={busy || cursors.length === 0}
                                    onClick={() =>
                                        setPage({
                                            companyId,
                                            cursors: cursors.slice(0, -1)
                                        })
                                    }
                                >
                                    {t("previousPage")}
                                </Button>
                                <Button
                                    disabled={busy || !state.nextCursor}
                                    onClick={() => {
                                        if (state.nextCursor)
                                            setPage({
                                                companyId,
                                                cursors: [
                                                    ...cursors,
                                                    state.nextCursor
                                                ]
                                            });
                                    }}
                                >
                                    {t("nextPage")}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Paper>
        </Page>
    );
}
